import type { RiskModelType } from "@unbox/db";
import type { BadgeColor } from "@/components/base/badges/badges";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import {
  type ComponentEditor,
  ENGINEERING_ONLY_BADGE,
  type RiskComponent,
  riskComponentsFor,
} from "@/components/safety-config/risk-components";

// Every component gets its own colour, distinct from the row's other badges
// (model type is gray, Vision is blue): customer parts are green, engineering
// is orange, and each console part gets its own hue.
const CONSOLE_LABEL_COLOR: Record<string, BadgeColor<"pill-color">> = {
  Detection: "indigo",
  Tolerance: "purple",
};

const EDITOR_COLOR: Record<ComponentEditor, BadgeColor<"pill-color">> = {
  customer: "success",
  console: "indigo",
  engineering: "orange",
};

const componentColor = (component: RiskComponent): BadgeColor<"pill-color"> =>
  component.editor === "console"
    ? (CONSOLE_LABEL_COLOR[component.label] ?? EDITOR_COLOR.console)
    : EDITOR_COLOR[component.editor];

const EDITOR_LABELS: Record<ComponentEditor, string> = {
  customer: "Customer",
  console: "Console",
  engineering: "Engineering",
};

export const ComponentBadges = ({
  modelType,
  config,
}: {
  modelType: RiskModelType;
  config: Record<string, unknown>;
}) => {
  const components = riskComponentsFor(modelType, config);
  if (components.length === 0) {
    return (
      <Tooltip title={ENGINEERING_ONLY_BADGE.tooltip}>
        <TooltipTrigger className="cursor-default">
          <Badge size="sm" color={EDITOR_COLOR.engineering}>
            {ENGINEERING_ONLY_BADGE.label}
          </Badge>
        </TooltipTrigger>
      </Tooltip>
    );
  }
  return (
    <>
      {components.map((component) => (
        <Tooltip key={`${component.label}-${component.editor}`} title={component.tooltip}>
          <TooltipTrigger className="cursor-default">
            <Badge size="sm" color={componentColor(component)}>
              {`${component.label} · ${EDITOR_LABELS[component.editor]}`}
            </Badge>
          </TooltipTrigger>
        </Tooltip>
      ))}
    </>
  );
};
